import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Edit, Trash2, List, GripVertical } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import api from '../services/api';
import { toast } from 'sonner';

const FIELD_TYPE_OPTIONS = [
  { value: 'time', label: 'Čas' },
  { value: 'temperature', label: 'Teplota' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'text', label: 'Text' },
];

const AutoclaveProcessFieldsPage = () => {
  const navigate = useNavigate();
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [fieldToDelete, setFieldToDelete] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    field_type: 'time',
    order: 0,
    is_required: false,
    description: ''
  });

  useEffect(() => {
    fetchFields();
  }, []);

  const fetchFields = async () => {
    try {
      const res = await api.get('/autoclave/process-field-types/all');
      setFields(res.data);
    } catch (error) {
      toast.error('Chyba při načítání typů záznamů');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (field = null) => {
    if (field) {
      setEditingField(field);
      setFormData({
        name: field.name,
        field_type: field.field_type,
        order: field.order,
        is_required: field.is_required,
        description: field.description || ''
      });
    } else {
      setEditingField(null);
      const maxOrder = fields.length > 0 ? Math.max(...fields.map(f => f.order)) : 0;
      setFormData({ name: '', field_type: 'time', order: maxOrder + 1, is_required: false, description: '' });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      toast.error('Vyplňte název');
      return;
    }

    try {
      if (editingField) {
        await api.put(`/autoclave/process-field-types/${editingField.id}`, formData);
        toast.success('Typ záznamu upraven');
      } else {
        await api.post('/autoclave/process-field-types', formData);
        toast.success('Typ záznamu přidán');
      }
      setDialogOpen(false);
      fetchFields();
    } catch (error) {
      toast.error('Chyba při ukládání');
    }
  };

  const handleDelete = async () => {
    if (!fieldToDelete) return;
    try {
      await api.delete(`/autoclave/process-field-types/${fieldToDelete.id}`);
      toast.success('Typ záznamu smazán');
      setDeleteDialogOpen(false);
      setFieldToDelete(null);
      fetchFields();
    } catch (error) {
      toast.error('Chyba při mazání');
    }
  };

  const getFieldTypeLabel = (type) => {
    const option = FIELD_TYPE_OPTIONS.find(o => o.value === type);
    return option ? option.label : type;
  };

  const getFieldTypeColor = (type) => {
    switch (type) {
      case 'time': return 'bg-blue-100 text-blue-700';
      case 'temperature': return 'bg-orange-100 text-orange-700';
      case 'checkbox': return 'bg-green-100 text-green-700';
      case 'text': return 'bg-purple-100 text-purple-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Typy záznamů procesu</h1>
              <p className="text-sm text-slate-500">Nastavení polí pro evidenci procesu autoklávu</p>
            </div>
          </div>
          <Button onClick={() => handleOpenDialog()} className="bg-teal-600 hover:bg-teal-700">
            <Plus className="h-4 w-4 mr-2" />
            Přidat typ záznamu
          </Button>
        </div>

        {/* List */}
        {loading ? (
          <div className="text-center py-12">Načítání...</div>
        ) : fields.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <List className="h-12 w-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">Žádné typy záznamů</h3>
            <p className="text-slate-500 mb-4">Začněte přidáním prvního typu záznamu</p>
            <Button onClick={() => handleOpenDialog()} className="bg-teal-600 hover:bg-teal-700">
              <Plus className="h-4 w-4 mr-2" />
              Přidat typ záznamu
            </Button>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700 w-12">Pořadí</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700">Název</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700">Typ</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700">Popis</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700">Povinné</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700">Stav</th>
                  <th className="text-right px-4 py-3 text-sm font-semibold text-slate-700">Akce</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {fields.map((field) => (
                  <tr key={field.id} className={`hover:bg-slate-50 ${!field.is_active ? 'opacity-50' : ''}`}>
                    <td className="px-4 py-3 text-slate-500">
                      <div className="flex items-center gap-1">
                        <GripVertical className="h-4 w-4 text-slate-300" />
                        {field.order}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-900">{field.name}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getFieldTypeColor(field.field_type)}`}>
                        {getFieldTypeLabel(field.field_type)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-sm">{field.description || '-'}</td>
                    <td className="px-4 py-3">
                      {field.is_required ? (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">Ano</span>
                      ) : (
                        <span className="text-slate-400">Ne</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        field.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {field.is_active ? 'Aktivní' : 'Neaktivní'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(field)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-red-600 hover:text-red-700"
                          onClick={() => { setFieldToDelete(field); setDeleteDialogOpen(true); }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingField ? 'Upravit typ záznamu' : 'Nový typ záznamu'}</DialogTitle>
            <DialogDescription>
              {editingField ? 'Upravte nastavení typu záznamu' : 'Vyplňte nastavení nového typu záznamu procesu'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Název *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="např. Zapnutí"
              />
            </div>
            <div className="space-y-2">
              <Label>Typ pole *</Label>
              <Select
                value={formData.field_type}
                onValueChange={(value) => setFormData({ ...formData, field_type: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Vyberte typ" />
                </SelectTrigger>
                <SelectContent>
                  {FIELD_TYPE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Pořadí</Label>
              <Input
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
              />
            </div>
            <div className="space-y-2">
              <Label>Popis</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Volitelný popis..."
                rows={2}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="is_required"
                checked={formData.is_required}
                onCheckedChange={(checked) => setFormData({ ...formData, is_required: checked })}
              />
              <Label htmlFor="is_required" className="cursor-pointer">Povinné pole</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Zrušit</Button>
            <Button onClick={handleSubmit} className="bg-teal-600 hover:bg-teal-700">
              {editingField ? 'Uložit' : 'Vytvořit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Smazat typ záznamu?</AlertDialogTitle>
            <AlertDialogDescription>
              Opravdu chcete smazat typ záznamu "{fieldToDelete?.name}"? Tato akce je nevratná.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Zrušit</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Smazat
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
};

export default AutoclaveProcessFieldsPage;
